import React from 'react';
import PropTypes from 'prop-types';
import { Control, Form } from 'react-redux-form';
import {
  duckify,
  dependentDuck,
  filterDuck,
  singleDuck,
  withForm
} from 'utils/duckify';
import {
  billingAccountForm,
  billingAccounts,
  billingConfigs,
  billingItems,
  autopayInstallments,
  modals,
  paymentProfiles,
  users
} from 'ducks';
import {
  formatCurrency,
  titleize
} from 'utils';
import FA from 'utils/FA';
import Placeholder from 'components/Placeholder';
import ControlGroup from 'components/forms/ControlGroup';
import ModalRoot from 'components/ModalRoot';
import LinkButton from 'components/LinkButton/LinkButton';
import { PaymentProfileType, UserType } from 'propTypes';
import ModalComponent from 'components/ModalComponent';
import AutopayDateEditor, { AutopayDateDisplay } from './AutopayDateEditor';

const ConfirmAutoPay = ({ autopay, onConfirm }) => {
  const title = `Confirm ${autopay ? 'Enable' : 'Disable'} AutoPay`;
  const buttons = [
    <button
      type='button'
      onClick={() => onConfirm()}
      className={`btn ${autopay ? 'btn-primary' : 'btn-danger'}`}
    >
      {autopay ? 'Enable' : 'Disable'}
    </button>
  ];

  return (
    <ModalComponent title={title} buttons={buttons}>
      {autopay ? (
        <p>
          Are you sure you want to enable autopay for this payment method?
          If any other payment method has autopay enabled, this action will disable autopay for that payment method.
        </p>
      ) : (
        <p>
          Are you sure you want to disable autopay for this payment method?
          You will need to enable autopay on another payment method for autopay to be active.
        </p>
      )}
    </ModalComponent>
  );
};

ConfirmAutoPay.propTypes = {
  autopay: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired
};

const BillingAccountOverview = ({
  billingAccount,
  billingConfig,
  changeAutopay,
  changeLateFeeDisabled,
  duckMeta,
  userPaymentProfiles,
  showAudits,
  confirmAutoPayChange
}) => (
  <div className='billing-account-overview'>
    <Placeholder.text
      ready={billingAccount && duckMeta.userPaymentProfiles.ready && duckMeta.billingConfig.ready}
      width={325}
      lines={2}
      lineWidths={[60]}
      lastLineWidth={100}
      content={() => (
        <React.Fragment>
          <div className='billing-account-overview-balance'>
            Current Balance:
            &nbsp;
            {formatCurrency(billingAccount.current_balance)}
          </div>

          <React.Fragment>
            <p>
              <AutopayDateDisplay billingAccount={billingAccount} showExpiringDays />
            </p>
            <p>
              <AutopayDateDisplay billingAccount={billingAccount} />
              &nbsp;
              <LinkButton
                content='View Changes'
                onClick={() => showAudits({ resource_id: billingAccount.id, resource: 'billing_accounts' })}
              />
            </p>
          </React.Fragment>

          <Form model='billingAccountForm.model' className='form-horizontal'>
            <AutopayDateEditor billingAccount={billingAccount} billingConfig={billingConfig} isAdmin />
            {
              billingConfig.late_fee_enabled &&
              <div className='billing-account-overview-late_fees'>
                <ControlGroup
                  label={(
                    <React.Fragment>
                      Late Fees Disabled:
                    </React.Fragment>
                  )}
                >
                  <Control.checkbox model='.late_fee_disabled' onChange={(event) => changeLateFeeDisabled(event.target.checked)}/>
                  &nbsp;
                  <LinkButton
                    content="View Changes"
                    onClick={() => showAudits({resource_id: billingAccount.id, resource: 'billing_accounts'})}
                  />
                </ControlGroup>
              </div>
            }
          </Form>

          <div className='saved-payment-profiles'>
            <div className='billing-account-overview-profiles'>
              Saved Payment Profiles
            </div>
            {userPaymentProfiles.length === 0 && (
              <div>
                {billingAccount.user_id ? (
                  <React.Fragment>
                    There are no saved payment profiles.
                    &nbsp;
                    <LinkButton
                      content='View Changes'
                      onClick={() => showAudits({associated_id: billingAccount.user_id})}
                    />
                  </React.Fragment>
                ) : (
                  "This contact is not authenticated to a portal account, and has no payment profiles."
                )}
              </div>
            )}
            <table className='table'>
              <tbody>
                {userPaymentProfiles.map(p => (
                  <tr key={p.id} className='payment-profile'>
                    <td>
                      <FA name={p.payment_method === 'credit card' ? 'credit-card' : 'bank'} nbsp />
                      {titleize(p.payment_method)}
                    </td>
                    <td>{p.name}</td>
                    {p.payment_verification && p.payment_verification.status !== 'verified' && (
                      <td>
                        <FA name='exclamation-circle' nbsp />
                        Needs Verification
                      </td>
                    )}
                    <td>
                      <FA name={p.autopay ? 'check-square' : 'square-o'} nbsp />
                      Autopay
                    </td>
                    <td>
                      <LinkButton
                        content={p.autopay ? 'Disable Autopay' : 'Enable Autopay'}
                        onClick={() => confirmAutoPayChange({
                          autopay: !p.autopay,
                          onConfirm: () => changeAutopay({
                            id: p.id,
                            payment_method: p.payment_method, // required by controller
                            autopay: !p.autopay
                          })
                        })}
                      />
                    </td>
                    <td>
                      <LinkButton
                        content='View Changes'
                        onClick={() => showAudits({associated_id: billingAccount.user_id, resource_id: p.id})}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </React.Fragment>
      )}
    />
    <ModalRoot modals={{ ConfirmAutoPay }} />
  </div>
);

BillingAccountOverview.propTypes = {
  billingAccount: PropTypes.shape({
    id: PropTypes.string,
    current_balance: PropTypes.string
  }),
  billingConfig: PropTypes.shape({
    billing_day: PropTypes.number
  }),
  changeAutopay: PropTypes.func,
  duckMeta: PropTypes.shape({
    billingConfig: PropTypes.shape({
      ready: PropTypes.bool
    }),
    userPaymentProfiles: PropTypes.shape({
      ready: PropTypes.bool
    })
  }),
  userPaymentProfiles: PropTypes.arrayOf(PaymentProfileType),
  showAudits: PropTypes.func,
  confirmAutoPayChange: PropTypes.func
};

BillingAccountOverview.defaultProps = {
  billingAccount: {
    current_balance: ''
  },
  billingConfig: {
    billing_day: ''
  },
  changeAutopay: () => false,
  changeDay: () => false,
  duckMeta: {
    billingConfig: {
      ready: false
    },
    userPaymentProfiles: {
      ready: false
    }
  },
  userPaymentProfiles: [],
  showAudits: () => false,
  confirmAutoPayChange: () => false
};

const mapDucksToProps = (props) => {
  const billingAccount = singleDuck(billingAccounts, props.billingAccountId);
  const userPaymentProfiles = dependentDuck(
    billingAccount,
    ({ value: { user_id } }) => filterDuck(paymentProfiles, { user_id })
  );

  return {
    billingAccount,
    billingAccountForm: withForm(billingAccount, billingAccountForm),
    billingConfig: singleDuck(billingConfigs, 'current'),
    userPaymentProfiles
  };
};

const mapDispatchToProps = (dispatch, { billingAccountId }) => ({
  changeAutopay: (profile) => dispatch(paymentProfiles.actions.update(profile))
    .then(() => dispatch(modals.actions.hide()))
    .then(() => dispatch(paymentProfiles.actions.markDirty()))
    .catch(response => {
      if (!response.billing_account) {
        Utility.displayFlashMessages({ error: 'Sorry, something went wrong.' });
      }
    }),
  changeLateFeeDisabled: (late_fee_disabled) =>
    dispatch(billingAccountForm.actions.save({
      id: billingAccountId,
      late_fee_disabled
    })).then(() => {
      dispatch(billingItems.actions.markDirty());
      dispatch(autopayInstallments.actions.markDirty());
      document.activeElement.blur();
    }).catch(response => {
      if (!response.billing_account) {
        Utility.displayFlashMessages({ error: 'Sorry, something went wrong.' });
      }
    }),
  showAudits: ({associated_id, resource='payment_profiles', resource_id}) => {
    const props = {
      associated_id,
      resource
    };
    if (resource_id) {
      props.id = resource_id;
    }
    dispatch(modals.actions.show('AuditViewer', props));
  },
  confirmAutoPayChange: (modalProps) => {
    return dispatch(modals.actions.show('ConfirmAutoPay', modalProps));
  }
});

export default duckify(mapDucksToProps, () => ({}), mapDispatchToProps)(BillingAccountOverview);
